class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
    this.productsToShow = parseInt(this.dataset.productsToShow) || 4;
    this.storageKey = 'shopify_recently_viewed_products';
    this.maxProducts = 50;
  }

  connectedCallback() {
    this.loadProducts();
  }

  getViewedProducts() {
    try {
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load recently viewed products:', e);
    }
    return [];
  }

  async loadProducts() {
    const viewedProducts = this.getViewedProducts();

    if (viewedProducts.length === 0) {
      this.showEmptyState();
      return;
    }

    // 現在の商品ページの商品を除外
    const currentProductHandle = this.getCurrentProductHandle();
    const filteredProducts = viewedProducts.filter((p) => {
      if (currentProductHandle && p.handle === currentProductHandle) {
        return false;
      }
      return true;
    });

    if (filteredProducts.length === 0) {
      this.showEmptyState();
      return;
    }

    // 表示する商品数を制限
    const productsToShow = filteredProducts.slice(0, this.productsToShow);

    // Shopify Ajax APIを使って商品情報を取得して表示
    await this.renderProducts(productsToShow);
  }

  getCurrentProductHandle() {
    // 商品ページのURLからハンドルを取得
    if (window.location.pathname.startsWith('/products/')) {
      return window.location.pathname.split('/products/')[1].split('?')[0].split('#')[0];
    }
    return null;
  }

  async renderProducts(products) {
    try {
      const productsHTML = await Promise.all(
        products.map(async (product, index) => {
          return await this.fetchProductCard(product, index);
        }),
      );

      const container = this.querySelector('.recently-viewed-products__empty');
      if (container) {
        container.style.display = 'none';
      }

      const columnsDesktop = parseInt(this.dataset.columnsDesktop) || 4;
      const columnsMobile = parseInt(this.dataset.columnsMobile) || 2;
      const showMobileSlider = this.dataset.swipeOnMobile === 'true' && products.length > parseInt(columnsMobile);

      const sliderHTML = `
        <slider-component class="slider-mobile-gutter">
          <ul
            id="Slider-${this.sectionId}"
            class="grid product-grid contains-card contains-card--product grid--${columnsDesktop}-col-desktop grid--${columnsMobile}-col-tablet-down${showMobileSlider ? ' slider slider--tablet grid--peek' : ''}"
            role="list"
          >
            ${productsHTML.join('')}
          </ul>
          ${showMobileSlider ? this.renderSliderButtons(products.length) : ''}
        </slider-component>
      `;

      this.insertAdjacentHTML('beforeend', sliderHTML);

      // スライダーの初期化が必要な場合はここで実行
      if (showMobileSlider && typeof window.initSlider !== 'undefined') {
        window.initSlider();
      }
    } catch (error) {
      console.error('Failed to render recently viewed products:', error);
      this.showEmptyState();
    }
  }

  async fetchProductCard(product, index) {
    try {
      // Shopify Ajax APIを使って商品情報を取得
      const response = await fetch(`/products/${product.handle}.js`);
      if (!response.ok) {
        throw new Error('Failed to fetch product');
      }

      const productData = await response.json();
      return this.renderProductCard(productData, index);
    } catch (error) {
      console.error('Failed to fetch product:', error);
      // フォールバック: 保存された情報を使って表示
      return this.renderProductCardFallback(product, index);
    }
  }

  renderProductCard(product, index) {
    const featuredImage = product.featured_image || (product.images && product.images[0]) || '';
    const secondaryImage =
      this.dataset.showSecondaryImage === 'true' && product.images && product.images[1] ? product.images[1] : '';
    const showVendor = this.dataset.showVendor === 'true';
    const price = product.price || 0;
    const compareAtPrice = product.compare_at_price || 0;
    const moneyFormat = window.Shopify?.formatMoney || ((price) => `¥${(price / 100).toLocaleString()}`);

    const imageRatio = this.dataset.imageRatio || 'adapt';
    let ratioPercent = 100;
    if (imageRatio === 'portrait') {
      ratioPercent = 80;
    } else if (imageRatio === 'square') {
      ratioPercent = 100;
    }

    return `
      <li
        id="Slide-${this.sectionId}-${index + 1}"
        class="grid__item"
      >
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard ${featuredImage ? 'card--media' : 'card--text'}">
            ${
              featuredImage
                ? `
              <div class="card__inner ratio" style="--ratio-percent: ${ratioPercent}%;">
                <div class="card__media">
                  <div class="media media--transparent${secondaryImage ? ' media--hover-effect' : ''}">
                    <img
                      srcset="${this.getImageSrcset(featuredImage)}"
                      src="${featuredImage}"
                      sizes="(min-width: 990px) calc((100vw - 130px) / ${this.dataset.columnsDesktop}), (min-width: 750px) calc((100vw - 120px) / ${this.dataset.columnsMobile}), calc((100vw - 35px) / ${this.dataset.columnsMobile})"
                      alt="${this.escapeHtml(product.title)}"
                      class="motion-reduce"
                      loading="lazy"
                      width="533"
                      height="533"
                    >
                    ${
                      secondaryImage
                        ? `
                      <img
                        srcset="${this.getImageSrcset(secondaryImage)}"
                        src="${secondaryImage}"
                        sizes="(min-width: 990px) calc((100vw - 130px) / ${this.dataset.columnsDesktop}), (min-width: 750px) calc((100vw - 120px) / ${this.dataset.columnsMobile}), calc((100vw - 35px) / ${this.dataset.columnsMobile})"
                        alt="${this.escapeHtml(product.title)}"
                        class="motion-reduce"
                        loading="lazy"
                        width="533"
                        height="533"
                      >
                    `
                        : ''
                    }
                  </div>
                </div>
              </div>
            `
                : ''
            }
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${product.url}" class="full-unstyled-link">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
                ${
                  showVendor && product.vendor
                    ? `
                  <div class="caption-with-letter-spacing light">${this.escapeHtml(product.vendor)}</div>
                `
                    : ''
                }
                <div class="price">
                  <div class="price__container">
                    ${
                      compareAtPrice > price
                        ? `
                      <div class="price__sale">
                        <span class="visually-hidden">Regular price</span>
                        <s class="price-item price-item--regular">${moneyFormat(compareAtPrice)}</s>
                        <span class="visually-hidden">Sale price</span>
                        <span class="price-item price-item--sale price-item--last">${moneyFormat(price)}</span>
                      </div>
                    `
                        : `
                      <div class="price__regular">
                        <span class="visually-hidden">Regular price</span>
                        <span class="price-item price-item--regular">${moneyFormat(price)}</span>
                      </div>
                    `
                    }
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  renderProductCardFallback(product, index) {
    const imageUrl = product.image || '';
    const moneyFormat = window.Shopify?.formatMoney || ((price) => `¥${(price / 100).toLocaleString()}`);

    return `
      <li
        id="Slide-${this.sectionId}-${index + 1}"
        class="grid__item"
      >
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--standard ${imageUrl ? 'card--media' : 'card--text'}">
            ${
              imageUrl
                ? `
              <div class="card__inner ratio">
                <div class="card__media">
                  <div class="media media--transparent">
                    <img
                      src="${imageUrl}"
                      alt="${this.escapeHtml(product.title)}"
                      class="motion-reduce"
                      loading="lazy"
                      width="533"
                      height="533"
                    >
                  </div>
                </div>
              </div>
            `
                : ''
            }
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${product.url}" class="full-unstyled-link">
                    ${this.escapeHtml(product.title)}
                  </a>
                </h3>
                <div class="price">
                  <div class="price__container">
                    <div class="price__regular">
                      <span class="price-item price-item--regular">${moneyFormat(product.price || 0)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>
    `;
  }

  renderSliderButtons(total) {
    const caretIcon =
      '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-caret" viewBox="0 0 10 6"><path fill-rule="evenodd" clip-rule="evenodd" d="m9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z" fill="currentColor"></path></svg>';
    return `
      <div class="slider-buttons large-up-hide">
        <button
          type="button"
          class="slider-button slider-button--prev"
          name="previous"
          aria-label="Previous slide"
          aria-controls="Slider-${this.sectionId}"
        >
          <span class="svg-wrapper">
            ${caretIcon}
          </span>
        </button>
        <div class="slider-counter caption">
          <span class="slider-counter--current">1</span>
          <span aria-hidden="true"> / </span>
          <span class="visually-hidden">of</span>
          <span class="slider-counter--total">${total}</span>
        </div>
        <button
          type="button"
          class="slider-button slider-button--next"
          name="next"
          aria-label="Next slide"
          aria-controls="Slider-${this.sectionId}"
        >
          <span class="svg-wrapper">
            ${caretIcon}
          </span>
        </button>
      </div>
    `;
  }

  getImageSrcset(imageUrl) {
    if (!imageUrl) return '';
    const widths = [165, 360, 533, 720, 940, 1066];
    return widths.map((width) => `${imageUrl}?width=${width} ${width}w`).join(', ');
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  showEmptyState() {
    const container = this.querySelector('.recently-viewed-products__empty');
    if (container) {
      container.style.display = 'block';
    }
  }
}

customElements.define('recently-viewed-products', RecentlyViewedProducts);

// 商品ページで商品情報を保存
(function () {
  function saveCurrentProduct() {
    if (!window.location.pathname.startsWith('/products/')) {
      return;
    }

    const productHandle = window.location.pathname.split('/products/')[1].split('?')[0].split('#')[0];
    if (!productHandle) {
      return;
    }

    const storageKey = 'shopify_recently_viewed_products';
    let viewedProducts = [];

    try {
      const stored = localStorage.getItem(storageKey);
      if (stored) {
        viewedProducts = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load recently viewed products:', e);
      return;
    }

    // 既に存在する場合は削除
    viewedProducts = viewedProducts.filter((p) => p.handle !== productHandle);

    // 商品情報を取得
    const productInfo = document.querySelector('product-info');
    if (!productInfo) {
      return;
    }

    const productId = productInfo.dataset.productId;
    const productUrl = productInfo.dataset.url || window.location.href;

    // 商品タイトルを取得
    const productTitle = document.querySelector('h1.product__title, h1.h1, .product__title')?.textContent?.trim() || '';

    // 商品画像を取得
    let productImage = '';
    const productMediaImg = document.querySelector(
      '.product__media img, .product-media img, .media-gallery img, [data-media-id] img',
    );
    if (productMediaImg) {
      productImage = productMediaImg.src || productMediaImg.dataset.src || '';
      // サイズを調整（小さいサイズで保存）
      if (productImage && productImage.includes('?')) {
        productImage = productImage.split('?')[0] + '?width=533';
      }
    }

    // 商品価格を取得
    const priceElement = document.querySelector('.price__regular .price-item, .price .price-item--regular');
    const comparePriceElement = document.querySelector(
      '.price__sale .price-item--regular, .price .price-item--compare',
    );
    let price = 0;
    let compareAtPrice = 0;

    if (priceElement) {
      const priceText = priceElement.textContent.replace(/[^0-9]/g, '');
      price = parseInt(priceText) || 0;
    }

    if (comparePriceElement) {
      const comparePriceText = comparePriceElement.textContent.replace(/[^0-9]/g, '');
      compareAtPrice = parseInt(comparePriceText) || 0;
    }

    // ベンダーを取得
    const vendorElement = document.querySelector('.product__vendor, .vendor');
    const vendor = vendorElement?.textContent?.trim() || '';

    // 現在の商品情報を追加
    const productData = {
      id: productId || '',
      handle: productHandle,
      title: productTitle,
      url: productUrl,
      image: productImage,
      price: price,
      compare_at_price: compareAtPrice,
      available: true,
      vendor: vendor,
      timestamp: Date.now(),
    };

    viewedProducts.unshift(productData);

    // 最大50件まで保存
    if (viewedProducts.length > 50) {
      viewedProducts = viewedProducts.slice(0, 50);
    }

    try {
      localStorage.setItem(storageKey, JSON.stringify(viewedProducts));
    } catch (e) {
      console.error('Failed to save recently viewed products:', e);
    }
  }

  // DOMContentLoadedまたはページロード時に実行
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', saveCurrentProduct);
  } else {
    saveCurrentProduct();
  }
})();
